import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, CheckCircle2, Layers, RefreshCw, Code2, Network, LifeBuoy, Factory, FlaskConical, Zap, Landmark, Building2, Fuel, ShoppingBag, Truck } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const IMG = {
  heroBuilding: "https://images.unsplash.com/photo-1621831337128-35676ca30868?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBnbGFzcyUyMG9mZmljZSUyMGJ1aWxkaW5nJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzgxMjY5NDg5fDA&ixlib=rb-4.1.0&q=85",
  teamOffice: "https://images.pexels.com/photos/12903168/pexels-photo-12903168.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  teamCollab: "https://images.pexels.com/photos/3861563/pexels-photo-3861563.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  dataCenter: "https://images.unsplash.com/photo-1584169417032-d34e8d805e8b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxkYXRhJTIwY2VudGVyJTIwZW50ZXJwcmlzZXxlbnwwfHx8fDE3ODEyNjk0OTl8MA&ixlib=rb-4.1.0&q=85",
  abstractTech: "https://images.unsplash.com/photo-1770156812763-c1749bc0ebbf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHx0ZWNobm9sb2d5JTIwc2VydmVyJTIwcm9vbSUyMGFic3RyYWN0fGVufDB8fHx8MTc4MTI2OTQ4OXww&ixlib=rb-4.1.0&q=85",
};

const SERVICE_ICONS = {
  implementation: Layers,
  migrations: RefreshCw,
  "application-development": Code2,
  integration: Network,
  "support-maintenance": LifeBuoy,
};

const SERVICES = [
  { slug: "implementation", title: "Implementation", summary: "End-to-end SAP programs led by senior architects — discovery to hypercare." },
  { slug: "migrations", title: "Migrations", summary: "ECC → S/4HANA, on-prem to cloud, and Unicode upgrades — engineered for zero-drama cutover." },
  { slug: "application-development", title: "Application Development", summary: "ABAP, Fiori / UI5 and BTP extensions that keep the digital core clean." },
  { slug: "integration", title: "Integration", summary: "Integration Suite, CPI and API-led integrations across the enterprise stack." },
  { slug: "support-maintenance", title: "Support & Maintenance", summary: "24×7 SLA-backed application management for steady-state landscapes." },
];

const INDUSTRIES = [
  { Icon: Factory, name: "Manufacturing" },
  { Icon: FlaskConical, name: "Pharma & Life Sciences" },
  { Icon: Zap, name: "Energy & Utilities" },
  { Icon: Landmark, name: "Banking & Finance" },
  { Icon: Building2, name: "Public Sector" },
  { Icon: Fuel, name: "Oil & Gas" },
  { Icon: ShoppingBag, name: "Consumer Packaged Goods" },
  { Icon: Truck, name: "Logistics" },
];

const LOGOS = ["SAP S/4HANA", "RISE with SAP", "BTP", "Fiori", "HANA", "ABAP", "CPI", "Ariba", "SuccessFactors", "Concur"];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-gray-200">
        <div className="absolute inset-0 -z-10">
          <img
            src={IMG.heroBuilding}
            alt="Modern corporate glass building"
            className="w-full h-full object-cover editorial-img"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-white/85 to-white/40" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-28 lg:py-40">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-8">
              <Reveal>
                <div className="overline mb-8">SAP · S/4HANA · Cloud · Enterprise</div>
              </Reveal>
              <Reveal delay={120}>
                <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-[#0F172A]">
                  Enterprise SAP, <br />
                  delivered with <em className="not-italic underline decoration-2 decoration-[#111827] underline-offset-8">editorial</em> precision.
                </h1>
              </Reveal>
              <Reveal delay={240}>
                <p className="mt-8 max-w-2xl text-lg text-gray-600 leading-relaxed">
                  Sunsri Software is a specialist SAP consultancy partnering with growth-stage enterprises and global corporations on S/4HANA implementations, migrations, support, and training — engineered for outcomes, written like a thesis.
                </p>
              </Reveal>
              <Reveal delay={360}>
                <div className="mt-12 flex flex-wrap items-center gap-4">
                  <Link to="/contact" data-testid="hero-cta-primary" className="btn-gold">
                    Schedule a consultation <ArrowUpRight size={16} />
                  </Link>
                  <Link to="/services" data-testid="hero-cta-secondary" className="btn-outline">
                    Explore our services <ArrowRight size={16} />
                  </Link>
                </div>
              </Reveal>
            </div>

            <div className="md:col-span-4 hidden md:flex flex-col justify-end gap-6">
              <Reveal delay={520}>
                <div className="border-l-2 border-[#111827] pl-5">
                  <div className="font-serif text-4xl text-[#0F172A]">10+</div>
                  <div className="overline mt-1">Countries served</div>
                </div>
              </Reveal>
              <Reveal delay={620}>
                <div className="border-l-2 border-[#111827] pl-5">
                  <div className="font-serif text-4xl text-[#0F172A]">98%</div>
                  <div className="overline mt-1">SLA compliance · AMS</div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Logo marquee */}
      <section className="border-b border-gray-200 py-10 overflow-hidden bg-white">
        <div className="overline px-6 lg:px-8 mb-6 max-w-7xl mx-auto">Anchored in the SAP ecosystem</div>
        <div className="relative overflow-hidden">
          <div className="marquee-track flex gap-16 whitespace-nowrap will-change-transform" style={{ width: "200%" }}>
            {[...LOGOS, ...LOGOS].map((l, i) => (
              <span key={i} className="font-serif text-2xl text-gray-400 tracking-tight">{l}</span>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="py-24 sm:py-32 border-b border-gray-200" data-testid="home-services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-5">
              <div className="overline">What we do</div>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight mt-5 text-[#0F172A]">
                Eight disciplines. <br />One SAP partner.
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 self-end">
              <p className="text-lg text-gray-600 leading-relaxed">
                Whether you are starting an S/4HANA program or stabilising a global landscape, we bring senior consultants, repeatable methods, and an obsession with measurable outcomes.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            {SERVICES.map((s, idx) => {
              const Icon = SERVICE_ICONS[s.slug];
              const span = idx === 0 ? "md:col-span-8" : "md:col-span-4";
              const tall = idx === 0 ? "md:row-span-2" : "";
              return (
                <Reveal key={s.slug} delay={idx * 80} className={`${span} ${tall}`}>
                  <Link
                    to="/services"
                    data-testid={`service-card-${s.slug}`}
                    className="group relative block h-full bg-white border border-gray-200 p-8 lg:p-10 transition-all duration-300 hover:-translate-y-1 hover:border-gray-400"
                  >
                    <div className="flex items-start justify-between">
                      <Icon size={28} className="text-[#111827]" strokeWidth={1.5} />
                      <ArrowUpRight size={18} className="text-gray-400 group-hover:text-[#111827] transition" />
                    </div>
                    <h3 className="font-serif text-2xl lg:text-3xl mt-10 text-[#0F172A] leading-tight">{s.title}</h3>
                    <p className="text-gray-600 mt-3 leading-relaxed text-[15px]">{s.summary}</p>
                    {idx === 0 && (
                      <div className="mt-10 grid grid-cols-2 gap-3 text-sm text-gray-700">
                        {["Greenfield", "Brownfield", "RISE / GROW", "Activate"].map((t) => (
                          <div key={t} className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[#111827]" /> {t}</div>
                        ))}
                      </div>
                    )}
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ABOUT split */}
      <section className="py-24 sm:py-32 border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-6 order-2 md:order-1">
              <div className="overline">Who we are</div>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-5 text-[#0F172A] leading-tight">
                A boutique SAP firm with global ambition.
              </h2>
              <p className="text-lg text-gray-600 mt-6 leading-relaxed">
                We are former CIOs, SAP architects, and process specialists who believe enterprise transformation deserves the same craft as great editorial design. We build durable SAP landscapes that earn their place in the boardroom.
              </p>
              <div className="grid grid-cols-2 gap-6 mt-10">
                {["Senior-only delivery teams", "Fixed-price options", "Outcome-based contracts", "Global delivery footprint"].map((t) => (
                  <div key={t} className="flex items-start gap-3"><CheckCircle2 size={18} className="text-[#111827] mt-0.5" /><span className="text-[15px] text-gray-700">{t}</span></div>
                ))}
              </div>
              <Link to="/about" data-testid="home-about-link" className="link-underline inline-flex items-center gap-2 mt-10 text-[#111827] font-medium">
                More about Sunsri <ArrowRight size={16} />
              </Link>
            </div>
            <div className="md:col-span-6 order-1 md:order-2">
              <div className="relative">
                <img src={IMG.teamOffice} alt="Modern collaborative office" className="w-full h-[520px] object-cover editorial-img" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-24 sm:py-32 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <div className="overline">Industries we serve</div>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-5 text-[#0F172A] leading-tight">Domain expertise that scales.</h2>
            </div>
            <Link to="/industries" data-testid="home-industries-link" className="link-underline text-[#111827] font-medium inline-flex items-center gap-2">
              All industries <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-px bg-gray-200 border border-gray-200">
            {INDUSTRIES.map(({ Icon, name }) => (
              <div key={name} className="bg-white p-6 lg:p-7 flex flex-col items-start gap-4 hover:bg-gray-50 transition min-h-[170px]">
                <Icon size={24} strokeWidth={1.5} className="text-[#111827]" />
                <div className="font-serif text-[15px] lg:text-base text-[#0F172A] leading-tight">{name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 sm:py-32 border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
            <div className="md:col-span-5">
              <div className="overline">How we work</div>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-5 text-[#0F172A] leading-tight">A methodology that respects your budget and your boardroom.</h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 self-end">
              <p className="text-lg text-gray-600 leading-relaxed">Four phases. Clear deliverables. Senior consultants in the room from kickoff to hypercare — never a bait-and-switch.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            {[
              { n: "01", t: "Discover", d: "Stakeholder interviews, current-state assessment, and a thesis-driven roadmap." },
              { n: "02", t: "Design", d: "Fit-to-standard workshops, solution architecture, and a costed delivery plan." },
              { n: "03", t: "Deliver", d: "Activate-based sprints with weekly demos, integrated testing, and clean cutover." },
              { n: "04", t: "Sustain", d: "Hypercare, AMS, continuous improvement, and value tracking against KPIs." },
            ].map((p) => (
              <div key={p.n} className="border-t border-[#111827] pt-6">
                <div className="font-mono text-sm text-gray-500">{p.n}</div>
                <div className="font-serif text-2xl mt-3 text-[#0F172A]">{p.t}</div>
                <p className="text-[15px] text-gray-600 mt-3 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0F172A] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 sm:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-8">
            <div className="overline text-gray-400">Ready to begin?</div>
            <h2 className="font-serif text-4xl sm:text-5xl mt-5 leading-tight">Tell us about your SAP ambitions. We will write the plan.</h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/contact" data-testid="home-bottom-cta" className="inline-flex items-center gap-3 bg-white text-[#0F172A] px-7 py-4 text-sm font-medium hover:bg-gray-100 transition">
              Start a conversation <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
